import { useState } from "react";
import { DatePicker, Select, message } from "antd";
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import dayjs from "dayjs";
import useCities from "../../api/common/common-hooks";
import { useStatisticsRound } from "../../api/statistics/statistics";
import { useTranslation } from 'react-i18next';
import './style.css';


const Analytics1688 = ({ translation, language }) => {
  const { t } = useTranslation();
  const today = dayjs();
  const lastMonth = today.subtract(1, "month");
  const { RangePicker } = DatePicker;
  const [dateSorted, setDateSorted] = useState([lastMonth, today]);
  const [selectedCities, setSelectedCities] = useState([""]);
  const { data: cities } = useCities();
  const { data: statisticsRoundData } = useStatisticsRound({
    startDate: dateSorted[0].format('YYYY-MM-DDT00:00:00'),
    endDate: dateSorted[1].format('YYYY-MM-DDT23:59:59'),
    cityId: selectedCities.includes("") ? [] : selectedCities
  });
  const COLORS = ['#0000FF', '#4A86E8'];
  const RADIAN = Math.PI / 180;


  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const onChange = (dates) => {
    setDateSorted(dates);
  };

  const onChangeCities = (selectedValues) => {
    if (selectedValues === undefined) {
      setSelectedCities([]);
      return;
    }

    const values = Array.isArray(selectedValues) ? selectedValues : [selectedValues];

    if (values.includes("")) {
      setSelectedCities([""]);
      return;
    }

    if (selectedCities.includes("") && values.length > 0) {
      setSelectedCities(values);
      return;
    }

    if (values.length > 3) {
      message.warning(t('roundStatistics.maxCitiesWarning'));
      return;
    }

    setSelectedCities(values);
  };

  return (
    <div style={{ margin: '0 auto', width: '100%', display: "flex", flexDirection: 'column', alignItems: 'center' }}>
      <div className="analitics">
        <div className="analitics__content">
          <h1 className="analitics__title">{t('roundStatistics.analyticsTitle')}</h1>

          <div className="analitics__statistics">
            <div className="analitics__statistics-header">
              <div className="analitics__statistics-header__text-block">
                <div className="analitics__statistics-header__title">{t('roundStatistics.ordersData')}</div>

                <div className="analitics__statistics-header__store-block">
                  <div className="analitics__statistics-header__store-item">
                    <div className="analitics__statistics-header__store-round-1688"></div>
                    <div className="analitics__statistics-header__store-text">1688</div>
                  </div>

                  <div className="analitics__statistics-header__store-item">
                    <div className="analitics__statistics-header__store-round-pinduoduo"></div>
                    <div className="analitics__statistics-header__store-text">Pinduoduo</div>
                  </div>
                </div>
              </div>

              <div className="analitics__statistics-header__text-select">
                <Select
                  
                  mode={selectedCities.includes("") ? undefined : "multiple"}
                  value={selectedCities}
                  onChange={onChangeCities}
                  placeholder={t('roundStatistics.selectCity')}
                  style={{ minWidth: 200, height: 46, borderRadius: 8,}}
                  showSearch={false}
                  maxTagCount={3}
                  allowClear
                  options={[
                    { 
                      value: "", 
                      label: `${t('roundStatistics.allCities')}`,
                      disabled: selectedCities.length > 0 && !selectedCities.includes("") 
                    },
                    ...(cities?.map(city => ({
                      value: city.id,
                      label: city.name,
                    })) || [])
                  ]}
                />
                <RangePicker
                  style={{height: 46, borderRadius: 8}}
                  allowClear={false}
                  defaultValue={[lastMonth, today]}
                  format={{
                    format: 'YYYY-MM-DD',
                    type: 'mask',
                  }}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="analitics__statistics-body" style={{
              gap: selectedCities.length > 1 ? '50px' : '0',
              justifyContent: selectedCities.length > 2 ? 'space-between' : 'flex-start'
            }}>
                {selectedCities.map((city, index) => {
                  const roundData = statisticsRoundData?.[index];
                  
                  if (!roundData) return null; 

                  return (
                    <div className="analitics__statistics-body__round-item">
                      {roundData.marketplaces.every(entry => entry.value === 0) ? (
                        <div className="noData__round">{t('roundStatistics.noData')}</div>
                      ) : (
                        <ResponsiveContainer width={280} height={280} key={index}>
                          <PieChart>
                            <Pie
                              data={roundData.marketplaces}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={renderCustomizedLabel}
                              outerRadius={140}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {roundData.marketplaces?.map((entry, cellIndex) => (
                                <Cell key={`cell-${cellIndex}`} fill={COLORS[cellIndex % COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                      <div className="analitics__statistics-body__round-city">{roundData.cityName ? roundData.cityName : 'Все города'}</div>
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="analitics__table">
            <div className="analitics__table-header">
              <div className="analitics__table-header-item">{t('roundStatistics.city')}</div>
              <div className="analitics__table-header-item">{t('roundStatistics.products1688')}</div>
              <div className="analitics__table-header-item">{t('roundStatistics.productsPinduoduo')}</div>
              <div className="analitics__table-header-item">{t('roundStatistics.totalCount')}</div>
            </div>

            <div className="analitics__table-body">
              {selectedCities.map((city, index) => {

                const roundData = statisticsRoundData?.[index];
                  
                if (!roundData) return null;
                
                return (
                  <div className="analitics__table-body-item" key={city.id || index}>
                    <div className="analitics__table-body-value">{roundData.cityName ? roundData.cityName : 'Все города'}</div>
                    <di className="analitics__table-body-value">{roundData.marketplaces?.find(m => m.name === '1688').value} {t('roundStatistics.itemsUnit')}</di>
                    <div className="analitics__table-body-value">{roundData.marketplaces?.find(m => m.name === 'pinduoduo').value} {t('roundStatistics.itemsUnit')}</div>
                    <div className="analitics__table-body-value">{roundData.totalCount} {t('roundStatistics.itemsUnit')}</div>
                  </div>
                );
              })}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Analytics1688;
